import {KennethMustDie} from "../KennethMustDie";
import {KMDGameMode} from "../server/KMDGameMode";
import {gsap} from "gsap";
import {KMD_SYMBOL_ANIMATIONS, SPECIAL_SYMBOL_NAMES, X_HOLE_ANIMATIONS, X_NUDGE_ANIMATIONS} from "../server/IKMDData";
import {KMDGameAssets} from "../KMDGameAssets";
import {KMDSound} from "../sound/KMDSound";

class KMDSymbolParts extends PIXI.Container {

    private readonly _bg: PIXI.Sprite;
    private readonly _bgTo: PIXI.Sprite;
    private readonly _object: PIXI.Sprite;
    private readonly _objectTo: PIXI.Sprite;
    private readonly _frame: PIXI.Sprite;
    private readonly _spikeFrame: PIXI.Sprite;

    constructor() {
        super();

        this._bg = new PIXI.Sprite();
        this._bg.anchor.set(0.5);

        this._bgTo = new PIXI.Sprite();
        this._bgTo.anchor.set(0.5);

        this._object = new PIXI.Sprite();
        this._object.anchor.set(0.5);

        this._objectTo = new PIXI.Sprite();
        this._objectTo.anchor.set(0.5);

        this._frame = new PIXI.Sprite();
        this._frame.anchor.set(0.5);

        this._spikeFrame = new PIXI.Sprite();
        this._spikeFrame.anchor.set(0.5);

        this.addChild(this._bg, this._bgTo, this._object, this._objectTo, this._frame, this._spikeFrame);
        this._bgTo.visible = false;
        this._objectTo.visible = false;
    }

    public setTextures(symName: string, resizeLevel: number, splits: number): void {
        const mode: string = KennethMustDie.model.data.mode,
            splitParts: string = (resizeLevel === 1 ? "/Half_x" : "/x") + splits,
            bg: string = "/bg",
            object: string = "/object";
        let nameHelper: string = "/MG/",
            toNameHelper: string = "/FS/",
            frameNameHelper: string = "/MG/";

        this._spikeFrame.texture = KennethMustDie.assetLoader.getTextures("spike_frame/" + resizeLevel)[0];

        if (symName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
            this._object.texture = KennethMustDie.assetLoader.getTextures(symName + "/" + symName)[0];

            return;
        } else if (symName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            this._object.texture = KennethMustDie.assetLoader.getTextures(symName + "/" + symName)[0];
            this._frame.texture = KennethMustDie.assetLoader.getTextures("W" + frameNameHelper + resizeLevel)[0];

            return;
        } else if (["S", "S2", "W", "SM1", "SM2"].indexOf(symName) > -1) {
            toNameHelper = nameHelper;
        } else {
            if (mode === KMDGameMode.FREESPIN || mode === KMDGameMode.FREESPIN_AVALANCHE || mode === KMDGameMode.FREESPIN_COOL || mode === KMDGameMode.FREESPIN_COOL_AVALANCHE) {
                nameHelper = "/FS/";
                toNameHelper = "/SFS/";
            } else if (mode === KMDGameMode.FREESPIN_SUPER || mode === KMDGameMode.FREESPIN_SUPER_AVALANCHE) {
                nameHelper = frameNameHelper = toNameHelper = "/SFS/";
            }
        }

        this._bg.texture = KennethMustDie.assetLoader.getTextures(symName + bg + nameHelper + "0")[0];
        this._bgTo.texture = KennethMustDie.assetLoader.getTextures(symName + bg + toNameHelper + "0")[0];

        // #477
        if (KennethMustDie.useCensor && KennethMustDie.symbolsToBeCensored.includes(symName)) {
            nameHelper += "blur/";
            toNameHelper += "blur/";
        }

        this._object.texture = KennethMustDie.assetLoader.getTextures(symName + object + nameHelper + resizeLevel + splitParts)[0];
        this._objectTo.texture = KennethMustDie.assetLoader.getTextures(symName + object + toNameHelper + resizeLevel + splitParts)[0];

        if (symName === "M3") {
            symName = "L3";
        } else if (symName === "M4") {
            symName = "L4";
        } else if (symName === "M5") {
            symName = "L5";
        }
        this._frame.texture = KennethMustDie.assetLoader.getTextures(symName + frameNameHelper + resizeLevel)[0];

        let bgScale: number = 1;
        if (resizeLevel === 1) {
            bgScale = 0.67;
        } else if (resizeLevel === 2) {
            bgScale = 0.5;
        }
        this._bg.scale.y = this._bgTo.scale.y = bgScale;
    }

    public toggleBgVisibility(val: boolean): void {
        this._bg.visible = val;
    }

    public setToNormal(): void {
        this._bgTo.visible = this._objectTo.visible = false;

        this._bg.alpha = this._object.alpha = 1;
        this._bg.visible = this._object.visible = true;
    }

    public applyTint(value: number): void {
        this.children.forEach((child: PIXI.DisplayObject) => {
            (child as PIXI.Sprite).tint = value;
        });
    }

    public playInitialWinAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // can't define the tl in init for once, because of this shake/shift effect in x and y.

        tl.to(this, {
            duration: 1 / 30,
            x: this.x + 1
        }, 0.0667);
        tl.to(this, {
            duration: 1 / 30,
            x: this.x
        });

        tl.to(this, {
            duration: 1 / 30,
            y: this.y + 1
        }, 0.2333);
        tl.to(this, {
            duration: 1 / 30,
            y: this.y
        });

        tl.add(() => {
            this._bgTo.visible = this._objectTo.visible = true;
            this._bg.visible = this._object.visible = false;
        }, 0.1667);
        tl.add(() => {
            this._bg.visible = this._object.visible = true;
            this._bgTo.visible = this._objectTo.visible = false;
        }, 0.3333);
        tl.add(() => {
            this._bgTo.visible = this._objectTo.visible = true;
            this._bg.visible = this._object.visible = false;
        }, 0.4333);

        tl.add(() => {
            // to make the animation same as it was in spine
        }, 0.5);

        return tl;
    }

    public playToggleWinAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // can't define the tl in init for once only, because wild symbol doesn't play the toggle animation(for 1 symbol group) in that case

        tl.add(() => {
            this._bg.alpha = this._object.alpha = 1;
            this._bg.visible = this._object.visible = true;

            this._bgTo.alpha = this._objectTo.alpha = 0;
            this._bgTo.visible = this._objectTo.visible = true;
        });
        tl.to([this._bg, this._object], {
            duration: 0.5,
            alpha: 0
        });
        tl.to([this._bgTo, this._objectTo], {
            duration: 0.5,
            alpha: 1
        }, "<");

        tl.to([this._bgTo, this._objectTo], {
            duration: 0.5,
            alpha: 0
        });
        tl.to([this._bg, this._object], {
            duration: 0.5,
            alpha: 1
        }, "<");

        return tl;
    }
}

export class KMDSymbol extends PIXI.Container {
    private _parts: KMDSymbolParts;
    private _extras: KMDSymbolParts[] = [];
    private _extraXWaysFrames: PIXI.Sprite[] = [];
    private _symbolCount: PIXI.BitmapText;
    private _xNudgeSymbolCount: PIXI.BitmapText;
    private _extraAdditionalLines: PIXI.Sprite[] = [];
    private _animation: PIXI.spine.Spine;

    constructor(symbolName: string) {
        super();

        this._parts = new KMDSymbolParts();

        this._originalName = symbolName;
        if (symbolName === "XH") {
            symbolName = "L3";
        }

        if (symbolName === SPECIAL_SYMBOL_NAMES.NW) {
            symbolName = SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD;
        }
        this.name = symbolName;

        this._xNudgeSymbolCountValue = 0;
        this.resizeLevel = 0;
        this.splitParts = 1;

        // #351
        this._m1m2NameHelperXNudge = "";

        this._visitedByNudge = false;

        this.addChild(this._parts);

        this.setTextures();
    }

    private _splitParts: number;

    get splitParts(): number {
        return this._splitParts;
    }

    set splitParts(value: number) {
        value = Math.min(Math.max(value, 1), this._maxSplitParts);
        this._splitParts = value;
    }

    private _visitedByNudge: boolean;

    get visitedByNudge(): boolean {
        return this._visitedByNudge;
    }

    set visitedByNudge(value: boolean) {
        this._visitedByNudge = value;
    }

    private _xNudgeSymbolCountValue: number;

    get xNudgeSymbolCountValue(): number {
        return this._xNudgeSymbolCountValue;
    }

    private _m1m2NameHelperXNudge: string;

    get m1m2NameHelperXNudge(): string {
        return this._m1m2NameHelperXNudge;
    }

    set m1m2NameHelperXNudge(value: string) {
        this._m1m2NameHelperXNudge = value;
    }

    private _maxSplitParts: number;

    get maxSplitParts(): number {
        return this._maxSplitParts;
    }

    private _resizeLevel: number;

    get resizeLevel(): number {
        return this._resizeLevel;
    }

    set resizeLevel(value: number) {
        this._resizeLevel = value;

        this._maxSplitParts = KennethMustDie.SYMBOL_MAX_SPLIT_PARTS[value];
    }

    private _originalName: string = "";

    get originalName(): string {
        return this._originalName;
    }

    set originalName(value: string) {
        this._originalName = value;
    }

    public getExtrasLength(): number {
        return this._extras.length;
    }

    public setToNormalState(): void {
        this.removeNoWinTint();

        this._parts.setToNormal();
        this._extras.forEach((item: KMDSymbolParts) => {
            item.setToNormal();
        });

        this.checkBgVisibilityForLowSym(false);
    }

    public setSplitState(multi: number): void {
        this.splitParts = multi;

        this.setTextures();
    }

    public resizeAsPerXWays(resizeLevel: number, multi: number): void {
        this.resizeLevel = resizeLevel;

        if (this.name === SPECIAL_SYMBOL_NAMES.XH) {
            this.setState((resizeLevel === 1 ? KMD_SYMBOL_ANIMATIONS.HALF : KMD_SYMBOL_ANIMATIONS.SEVENTY_FIVE));

            return;
        }

        // #536
        if (this.name === SPECIAL_SYMBOL_NAMES.S) {
            this.showSymbolCount(multi);
        } else {
            this.splitParts = multi;
        }

        this.setTextures();
    }

    // #224
    public getNoWinStateAnimation(): void {
        const noWinTint: number = 0x777777;

        if (this.name === SPECIAL_SYMBOL_NAMES.XH) {
            let state: string = KMD_SYMBOL_ANIMATIONS.NO_WIN;
            if (this._resizeLevel === 1) {
                state = KMD_SYMBOL_ANIMATIONS.HALF_NO_WIN;
            } else if (this._resizeLevel === 2) {
                state = KMD_SYMBOL_ANIMATIONS.SEVENTY_FIVE_NO_WIN;
            }

            this.setState(state);

            return;
        }

        if (this._animation) {
            this._animation.visible = false;
        }
        this._parts.visible = true;

        this._parts.applyTint(noWinTint);
        this._extras.forEach((item: KMDSymbolParts) => {
            item.applyTint(noWinTint);
        });
        this.checkBgVisibilityForLowSym(true);

        this._extraXWaysFrames.forEach((item: PIXI.Sprite) => {
            item.tint = noWinTint;
        });
        this._extraAdditionalLines.forEach((item: PIXI.Sprite) => {
            item.tint = noWinTint;
        });
    }

    // #224
    public getPreWinStateAnimation(): gsap.core.Timeline {
        const relativeX: number = this.x + this.parent.x,
            x: number = this._parts.x + relativeX,
            relativeY: number = this.getRelativeY(),
            y: number = this.getY(),
            tl: gsap.core.Timeline = gsap.timeline();

        tl.add(KennethMustDie.winOverlays.playPreWinAnimation(this._resizeLevel, x, y), 0);
        if (this._extras.length) {
            this._extras.forEach((extra: KMDSymbolParts) => {
                tl.add(KennethMustDie.winOverlays.playPreWinAnimation(this._resizeLevel, extra.x + relativeX, extra.y + relativeY), 0);
            });
        }

        return tl;
    }

    public setInitialWinState(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            this._parts.playInitialWinAnimation(),
            this.playOverlayAnimation("symHighlight")
        ], 0);

        if (this._extras.length) {
            this._extras.forEach((part: KMDSymbolParts) => {
                tl.add(part.playInitialWinAnimation(), 0);
            });
        }

        return tl;
    }

    public setToggleState(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this._parts.playToggleWinAnimation(), 0);

        if (this._extras.length) {
            this._extras.forEach((part: KMDSymbolParts) => {
                tl.add(part.playToggleWinAnimation(), 0);
            });
        }

        return tl;
    }

    public setState(state: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (!this.visible) {
            return tl;
        }

        if (!this._animation) {
            this._animation = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(this.name));
            // this._animation = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(this.name + (KennethMustDie.useCensor && this.name === SPECIAL_SYMBOL_NAMES.XH ? "Blur" : ""))); // #477

            this._animation.x = this._parts.x;
            this._animation.y = this._parts.y;
        }

        if (!this._animation.state || !(this._animation.state.hasAnimation(state))) {
            return tl;
        }

        if (!this._animation.parent) {
            tl.add(() => {
                this._animation.x = this._parts.x;
                this._animation.y = this._parts.y;
                this.addChild(this._animation);

                this._animation.updateTransform();
            }, 0);
        }

        if (this._animation) {
            tl.add(() => {
                this._animation.visible = true;
            }, 0);
        }

        // #351
        if (this._m1m2NameHelperXNudge && this._originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            if (state.indexOf("fullWin") > -1) {
                if (this._m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.HELPER_300) {
                    state = KMD_SYMBOL_ANIMATIONS.WIN;
                } else if (this._m1m2NameHelperXNudge.includes("_200")) {
                    state = KMD_SYMBOL_ANIMATIONS.WIN + (this._m1m2NameHelperXNudge.split(KMD_SYMBOL_ANIMATIONS.NORMAL)[1]);
                } else {
                    state = (KMD_SYMBOL_ANIMATIONS.WIN + "_" + this._m1m2NameHelperXNudge);
                }
            } else if (state.indexOf("_toggle") > -1) {
                if (this._m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.HELPER_300) {
                    state = KMD_SYMBOL_ANIMATIONS.TOGGLE;
                } else {
                    state = (this._m1m2NameHelperXNudge + "_toggle");
                }
            }
        }

        if (state.indexOf(KMD_SYMBOL_ANIMATIONS.NORMAL) > -1 && this._originalName !== SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            this.resizeLevel = 0;

            if (this.name === SPECIAL_SYMBOL_NAMES.S) {
                this._animation.y = 0;
            }
        }

        if (this._m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.FULL_200 || this._m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.HELPER_300) {
            this.resizeLevel = 1;
        }

        if (state.indexOf(KMD_SYMBOL_ANIMATIONS.WIN) > -1 || state.indexOf(KMD_SYMBOL_ANIMATIONS.HALF_WIN) > -1 || state.indexOf(KMD_SYMBOL_ANIMATIONS.SEVENTY_FIVE_WIN) > -1) { // any type of win
            if (state === X_NUDGE_ANIMATIONS.FULL_WIN_2) { // this is only true for xNudgeWild
                tl.add(() => {
                    let y: number = 0;

                    if (this.y === (KennethMustDie.SYMBOL_HEIGHT * 2) && !this._m1m2NameHelperXNudge) {
                        y = -KennethMustDie.SYMBOL_HEIGHT;
                        this._xNudgeSymbolCount.y = ((-y) / 2) + (this._xNudgeSymbolCount.height / 2);
                    }

                    if (this._animation) {
                        this._animation.y = y;
                    }
                }, 0);
            }
        }

        // #351
        if ((state.indexOf("_ken") > -1) || (state.indexOf("_barbie") > -1)) {
            tl.add(() => {
                if (this.y === (KennethMustDie.SYMBOL_HEIGHT * 2)) {
                    this._xNudgeSymbolCount.y = -(KennethMustDie.SYMBOL_HEIGHT / 2) + (this._xNudgeSymbolCount.height / 2);
                }

                if (this._resizeLevel === 0 && this._animation) {
                    this._animation.y = 0;
                }
            }, 0);
        }

        tl.add(KennethMustDie.createTimelineForAnimation(this._animation, 0, state, false), 0);

        tl.add(() => {
            this._parts.visible = false;
        });

        return tl;
    }

    public playBlurStateForXH(): void {
        if (this._animation) {
            this._animation.autoUpdate = true;
            this._animation.state.setAnimation(0, X_HOLE_ANIMATIONS.BLUR, true);
        }
    }

    // #224
    public removeNoWinTint(): void {
        const normalTint: number = 0xffffff;

        this._parts.applyTint(normalTint);
        this._extras.forEach((item: KMDSymbolParts) => {
            item.applyTint(normalTint);
        });
        this.checkBgVisibilityForLowSym(false);

        this._extraXWaysFrames.forEach((item: PIXI.Sprite) => {
            item.tint = normalTint;
        });
        this._extraAdditionalLines.forEach((item: PIXI.Sprite) => {
            item.tint = normalTint;
        });
    }

    public getY(): number {
        const retVal: number = this.getRelativeY();

        if (this._originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            return (retVal + (this._resizeLevel === 1 ? (this._parts.y + 25) : 0));
        }
        return (retVal + this._parts.y);
    }

    public getRelativeY(): number {
        return (this.y + this.parent.y);
    }

    public playOverlayAnimation(assetName: string): gsap.core.Timeline {
        const relativeX: number = this.x + this.parent.x,
            x: number = this._parts.x + relativeX,
            relativeY: number = this.getRelativeY(),
            y: number = this.getY(),
            tl: gsap.core.Timeline = gsap.timeline(),
            shake: boolean = (assetName === "flicker");
        let state: KMD_SYMBOL_ANIMATIONS = KMD_SYMBOL_ANIMATIONS.WIN;

        if (this._resizeLevel === 1) {
            state = KMD_SYMBOL_ANIMATIONS.HALF_WIN;
        } else if (this._resizeLevel === 2) {
            state = KMD_SYMBOL_ANIMATIONS.SEVENTY_FIVE_WIN;
        }

        tl.add(KennethMustDie.winOverlays.playAnimation(state, x, y, assetName, shake), 0);
        if (this._extras.length) {
            this._extras.forEach((extra: KMDSymbolParts) => {
                tl.add(KennethMustDie.winOverlays.playAnimation(state, extra.x + relativeX, extra.y + relativeY, assetName, shake), 0);
            });
        }

        return tl;
    }

    public playXHolePotentialHighlight(): gsap.core.Timeline {
        const relativeX: number = this.x + this.parent.x,
            x: number = this._parts.x + relativeX,
            y: number = this.getY(),
            tl: gsap.core.Timeline = gsap.timeline();

        tl.add(KennethMustDie.winOverlays.playXHolePotentialHighlightAnimation(x, y), 0);

        return tl;
    }

    public playIgniteOverlayAnimation(reelId: number): gsap.core.Timeline {
        const relativeX: number = this.x + this.parent.x,
            x: number = this._parts.x + relativeX,
            relativeY: number = this.getRelativeY(),
            y: number = this.getY(),
            tl: gsap.core.Timeline = gsap.timeline();

        tl.add(KennethMustDie.winOverlays.playIgniteAnimation(this._resizeLevel, x, y, reelId), 0);
        if (this._extras.length) {
            this._extras.forEach((extra: KMDSymbolParts) => {
                tl.add(KennethMustDie.winOverlays.playIgniteAnimation(this._resizeLevel, extra.x + relativeX, extra.y + relativeY, reelId), 0);
            });
        }

        return tl;
    }

    public playHeartOverlayAnimation(): gsap.core.Timeline {
        const relativeX: number = this.x + this.parent.x,
            x: number = this._parts.x + relativeX,
            y: number = this.getY(),
            relativeY: number = this.getRelativeY(),
            tl: gsap.core.Timeline = gsap.timeline();
        let state: string = "150";

        if (this._resizeLevel === 1) {
            state = "100";
        } else if (this._resizeLevel === 2) {
            state = "75";
        }

        tl.add(KennethMustDie.winOverlays.playHeartAnimation(state, x, y), 0);
        if (this._extras.length) {
            this._extras.forEach((extra: KMDSymbolParts) => {
                tl.add(KennethMustDie.winOverlays.playHeartAnimation(state, extra.x + relativeX, extra.y + relativeY), 0);
            });
        }

        return tl;
    }

    public translateY(toY: number, useTl: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (useTl) {
            tl.to((this._animation && this._animation.visible ? this._animation : this._parts), {
                y: toY,
                duration: 3 / 30
            }, 0);
        } else {
            this._parts.y = toY;
        }

        tl.add(() => {
            if (this._animation) {
                this._animation.x = this._parts.x;
                this._animation.y = this._parts.y;
            }
        });

        return tl;
    }

    public revealXWays(name: string, additionalY: number, multi: number): gsap.core.Timeline {
        const additionalSymbols: number = (KennethMustDie.model.gameData.mysterySymbolSize - 1),
            tl: gsap.core.Timeline = gsap.timeline();
        let toY: number = (KennethMustDie.MAIN_REELS_TOP + (KennethMustDie.HEIGHT_PER_PART_X_WAYS * 3) - KennethMustDie.SPECIAL_REEL_TOP - (KennethMustDie.HEIGHT_PER_PART_X_WAYS / 4)) + additionalY,
            perPartHeight: number = KennethMustDie.SYMBOL_HEIGHT;

        if (additionalSymbols === 1) {
            toY += (KennethMustDie.HEIGHT_PER_PART_X_WAYS / 4);
        } else if (additionalSymbols === 2) { // resize to 100
            perPartHeight = KennethMustDie.HEIGHT_PER_PART_X_WAYS;
        } else if (additionalSymbols === 3) { // resize to 75
            perPartHeight = (KennethMustDie.SYMBOL_HEIGHT / 2);
            toY = toY + (KennethMustDie.HEIGHT_PER_PART_X_WAYS / 4) - (perPartHeight / 2);
        }

        this.name = name;
        this.resizeLevel = (additionalSymbols - 1);
        this.splitParts = multi;

        tl.add(() => {
            this._parts.setTextures(this.name, this._resizeLevel, this._splitParts);
            this._parts.y = toY;

            if (this._animation) {
                this._animation.visible = false;
            }
            this._parts.visible = true;

            this.resizeLevel = (additionalSymbols - 1);
        }, (5 / 30));

        this._extraXWaysFrames = [];
        for (let ctr: number = 1; ctr <= additionalSymbols; ctr++) {
            const item: KMDSymbolParts = new KMDSymbolParts();

            item.setTextures(this.name, this._resizeLevel, this._splitParts);
            item.y = (toY + (ctr * perPartHeight));

            tl.add(() => {
                this.addChild(item);

                // #272
                let textureNameHelper: string = name[1];
                if (name === "M1") {
                    textureNameHelper = name;
                } else if (name === "M2") {
                    textureNameHelper = name;
                }

                if (KennethMustDie.model.data.mode.indexOf(KMDGameMode.FREESPIN_SUPER) > -1) {
                    textureNameHelper = "Sepia_" + textureNameHelper;
                }

                const frameDivider: PIXI.Sprite = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("xways_frame/" + textureNameHelper)[0]);
                frameDivider.anchor.set(0.5);
                frameDivider.y = (item.y - (perPartHeight / 2));
                this.addChild(frameDivider);
                this._extraXWaysFrames.push(frameDivider);
            }, (5 / 30));
            this._extras.push(item);
        }

        this.checkBgVisibilityForLowSym(false);

        tl.add(this.displayLines(multi));

        return tl;
    }

    public revertResize(multi: number): void {
        this._extraAdditionalLines.forEach((val: PIXI.Sprite) => {
            if (val.parent) {
                val.parent.removeChild(val);
            }
        });
        this._extraAdditionalLines = [];

        if (this._extras.length) {
            this.revertXWays(multi);
        } else {
            if (this.name === SPECIAL_SYMBOL_NAMES.XH) {
                this.setState(KMD_SYMBOL_ANIMATIONS.NORMAL);
                this._animation.y = 0;
                this._parts.y = 0; // #565

                return;
            }
            this.resizeLevel = 0;
            if (["S", "S2"].indexOf(this.name) > -1) {
                this.splitParts = 1;
                this.showSymbolCount(multi);
            } else {
                this.splitParts = multi;

                this.displayLines(multi);
            }
            this.setTextures();
        }
        this._parts.y = 0;

        if (this._animation) {
            this._animation.y = 0;
        }
    }

    public updateTexture(name: string, multi: number = 1): void {
        this.removeNoWinTint();

        this.name = name;

        if (["S", "S2"].indexOf(name) > -1) {
            this.splitParts = 1;
            this.showSymbolCount(multi);
        } else {
            this.splitParts = multi;

            this.displayLines(multi);
        }
        this.setTextures();

        this._parts.visible = true;

        if (this._animation) {
            this._animation.visible = false;
        }
    }

    public updateAnimation(name: string, multi: number = 1): void {
        const prevY: number = ((this._animation && this._animation.visible) ? this._animation.y : this._parts.y);

        this.name = name;

        this.removeChild(this._animation);
        this._animation = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(this.name));
        // this._animation = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(this.name + (KennethMustDie.useCensor && this.name === SPECIAL_SYMBOL_NAMES.XH ? "Blur" : ""))); // #477
        this.addChild(this._animation);

        let state: string = KMD_SYMBOL_ANIMATIONS.NORMAL;
        if (this._resizeLevel === 1) {
            state = KMD_SYMBOL_ANIMATIONS.HALF;
        } else if (this._resizeLevel === 2) {
            state = KMD_SYMBOL_ANIMATIONS.SEVENTY_FIVE;
        }

        if (multi > 1) {
            if (["S", "S2", "X_NUDGE_WILD", "X_WAYS"].indexOf(name) === -1) {
                state += Math.min(multi, this.maxSplitParts);
            }

            this._animation.state.setAnimation(0, state, false);
            this.displayLines(multi);

            if (["S", "S2"].indexOf(name) > -1) {
                this.showSymbolCount(multi);
            }
        } else {
            this._animation.state.setAnimation(0, state, false);
        }

        this._animation.y = prevY;
    }

    public countMultiplierOnXNudgeInSpecialReel(toMultiValue: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < toMultiValue; i++) {
            tl.add(() => {
                if (i === 0) {
                    this.showMultiplierOnXNudge(1, -1);

                    // #442
                    if (toMultiValue > 1) {
                        KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_MULTI_COUNT_UP, 1, Number.MAX_VALUE);
                    }
                } else {
                    this._xNudgeSymbolCountValue += 1;

                    this._xNudgeSymbolCount.text = "x" + this._xNudgeSymbolCountValue;

                    // #442
                    if (i === (toMultiValue - 1)) {
                        KennethMustDie.soundPlayer.stopSound(KMDSound.BASEMENT_REEL_MULTI_COUNT_UP);

                        KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_MULTI_COUNT_UP_END);
                    }
                }
            }, (i * 0.1));
        }

        return tl;
    }

    public showMultiplierOnXNudge(multiValue: number, idx: number): void {
        const parent: PIXI.spine.Spine | KMDSymbolParts = (this._animation && this._animation.visible ? this._animation : this._parts);

        if (this._xNudgeSymbolCount && this._xNudgeSymbolCount.parent) {
            this._xNudgeSymbolCount.parent.removeChild(this._xNudgeSymbolCount);
        }

        this._xNudgeSymbolCountValue += multiValue;

        const yPos: number = (idx * KennethMustDie.SYMBOL_HEIGHT) + (KennethMustDie.SYMBOL_HEIGHT / 2);
        this._xNudgeSymbolCount = new PIXI.BitmapText("x" + this._xNudgeSymbolCountValue, {
            fontName: KMDGameAssets.BITMAP_FONT_WIN_CAP,
            fontSize: 24
        });
        this._xNudgeSymbolCount.anchor = new PIXI.Point(0.5, 0.5);
        this._xNudgeSymbolCount.position.set(0, yPos + this._xNudgeSymbolCount.height / 2);
        parent.addChild(this._xNudgeSymbolCount);
    }

    public showSymbolCount(multiValue: number): void {
        if (multiValue < 2) {
            return;
        }

        if (this._symbolCount) {
            multiValue = +(this._symbolCount.text.split("X")[1]);

            this._symbolCount.parent.removeChild(this._symbolCount);
        }

        const yPos: number = -(KennethMustDie.SYMBOL_HEIGHT / 2) + (this._resizeLevel === 0 ? 0 : (KennethMustDie.SYMBOL_HEIGHT / 6));
        this._symbolCount = new PIXI.BitmapText("X" + multiValue, {
            fontName: KMDGameAssets.BITMAP_FONT_SYM_COUNT,
            fontSize: 14
        });
        this._symbolCount.anchor = new PIXI.Point(0.5, 0.5);
        this._symbolCount.position.set(0, yPos + this._symbolCount.height / 2);
        this._parts.addChild(this._symbolCount);
    }

    public hideSymbolCount(): void {
        if (this._symbolCount) {
            this._symbolCount.visible = false;
        }
    }

    public removeMultiplierOnXNudge(): void {
        if (this._xNudgeSymbolCount && this._xNudgeSymbolCount.parent) {
            this._xNudgeSymbolCount.parent.removeChild(this._xNudgeSymbolCount);
        }
        this._xNudgeSymbolCountValue = 0;
    }

    public displayLines(multiValue: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this._extraAdditionalLines.forEach((val: PIXI.Sprite) => {
            if (val.parent) {
                val.parent.removeChild(val);
            }
        });
        this._extraAdditionalLines = [];

        if (multiValue > this._maxSplitParts) {
            const additionalLines: number = (multiValue - this._maxSplitParts),
                reducedY: number = 12;
            let lineTexture: string = this.name,
                yOffset: number = (KennethMustDie.SYMBOL_HEIGHT / this._maxSplitParts);

            switch (lineTexture) {
                case "M3":
                    lineTexture = "L3";
                    break;
                case "M4":
                    lineTexture = "L4";
                    break;
                case "M5":
                    lineTexture = "L5";
                    break;
            }

            // SFS or SFS_Avalanche
            if (this.name !== SPECIAL_SYMBOL_NAMES.W && this.name !== SPECIAL_SYMBOL_NAMES.SM1 && this.name !== SPECIAL_SYMBOL_NAMES.SM2 && KennethMustDie.model.data.mode.indexOf(KMDGameMode.FREESPIN_SUPER) > -1) {
                lineTexture += "_Sepia";
            }
            lineTexture += "_line";

            let symHeight: number = KennethMustDie.SYMBOL_HEIGHT,
                duration: number = 0.1; // #309
            if (this._resizeLevel === 1) {
                symHeight = KennethMustDie.HEIGHT_PER_PART_X_WAYS;
            } else if (this._resizeLevel === 2) {
                symHeight = (KennethMustDie.SYMBOL_HEIGHT / 2);
            }
            yOffset = ((symHeight - reducedY) / this._maxSplitParts);

            for (let ctr: number = 0; ctr < additionalLines; ctr++) {
                const line: PIXI.Sprite = new PIXI.Sprite(),
                    lineHighlight: PIXI.Sprite = new PIXI.Sprite(),
                    effectiveCtr: number = (ctr % this._maxSplitParts);

                this.modifyLine(line, lineHighlight, lineTexture, effectiveCtr, yOffset, symHeight, reducedY);

                tl.add(() => {
                    this._parts.addChild(line, lineHighlight);
                });
                tl.to([line.scale, lineHighlight.scale], {
                    x: 1,
                    duration: duration
                });
                tl.add(() => {
                    this._parts.removeChild(lineHighlight);
                });

                // #309
                if (ctr === this._maxSplitParts) {
                    duration *= 0.75;
                } else if (ctr === (2 * this._maxSplitParts)) {
                    duration *= 0.5;
                }
            }

            this._extras.forEach((extraItem: KMDSymbolParts) => {
                duration = 0.1;
                const extraSymLinesTl: gsap.core.Timeline = gsap.timeline(); // #309
                for (let ctr: number = 0; ctr < additionalLines; ctr++) {
                    const line: PIXI.Sprite = new PIXI.Sprite(),
                        lineHighlight: PIXI.Sprite = new PIXI.Sprite(),
                        effectiveCtr: number = (ctr % this._maxSplitParts);

                    this.modifyLine(line, lineHighlight, lineTexture, effectiveCtr, yOffset, symHeight, reducedY);

                    extraSymLinesTl.add(() => {
                        extraItem.addChild(line, lineHighlight);
                    });
                    extraSymLinesTl.to([line.scale, lineHighlight.scale], {
                        x: 1,
                        duration: duration
                    });
                    extraSymLinesTl.add(() => {
                        extraItem.removeChild(lineHighlight);
                    });

                    // #309
                    if (ctr === this._maxSplitParts) {
                        duration *= 0.75;
                    } else if (ctr === (2 * this._maxSplitParts)) {
                        duration *= 0.5;
                    }
                }
                tl.add(extraSymLinesTl, 0);
            });
        }

        return tl;
    }

    public displayMultiOnWin(multiValue: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            return tl;
        }

        if (multiValue > 1) {
            const multiItem: PIXI.BitmapText = new PIXI.BitmapText("X" + multiValue, {
                fontName: KMDGameAssets.BITMAP_FONT_SYM_COUNT,
                fontSize: 16
            });
            multiItem.alpha = 0;
            multiItem.pivot.set(multiItem.width / 2, multiItem.height / 2);

            tl.add(() => {
                this._parts.addChild(multiItem);
            }, 0);
            tl.to(multiItem, {
                duration: 0.1,
                alpha: 1
            }, 0);
            tl.to(multiItem, {
                duration: 0.1,
                alpha: 0
            }, 0.3);
            tl.add(() => {
                this._parts.removeChild(multiItem);
            });

            this._extras.forEach((extraItem: KMDSymbolParts) => {
                const extraMultiItem: PIXI.BitmapText = new PIXI.BitmapText("X" + multiValue, {
                    fontName: KMDGameAssets.BITMAP_FONT_SYM_COUNT,
                    fontSize: 16
                });
                extraMultiItem.alpha = 0;
                extraMultiItem.pivot.set(extraMultiItem.width / 2, extraMultiItem.height / 2);

                tl.add(() => {
                    extraItem.addChild(extraMultiItem);
                }, 0);
                tl.to(extraMultiItem, {
                    duration: 0.1,
                    alpha: 1
                }, 0);
                tl.to(extraMultiItem, {
                    duration: 0.1,
                    alpha: 0
                }, 0.3);
                tl.add(() => {
                    extraItem.removeChild(extraMultiItem);
                });
            });
        }

        return tl;
    }

    // #331
    public setInteractive(val: boolean): void {
        if (["W", "S", "X_WAYS", "S2", "X_NUDGE_WILD", "XH", "SM1", "SM2"].indexOf(this.name) > -1) {
            return;
        }

        this.setInteractiveOnItem(val, this._parts);
        this._extras.forEach((item: KMDSymbolParts) => {
            this.setInteractiveOnItem(val, item);
        });
    }

    private setInteractiveOnItem(val: boolean, item: KMDSymbolParts): void {
        item.interactive = val;
        item.buttonMode = val;
        if (val) {
            item.on("pointertap", () => KennethMustDie.miniPayTable.onClicked({
                dispObj: item,
                name: this.name,
                resizeLevel: this._resizeLevel
            }));
        } else {
            item.off("pointertap");
        }
    }

    private revertXWays(multi: number): void {
        this.removeChildren();
        this._extras = [];
        this._extraXWaysFrames = [];

        this._parts = new KMDSymbolParts();
        this.name = SPECIAL_SYMBOL_NAMES.X_WAYS;
        this.resizeLevel = 0;
        this.splitParts = 1;
        this.setTextures();
        this.addChild(this._parts);

        this.showSymbolCount(multi);
    }

    private setTextures(): void {
        this._parts.setTextures(this.name, this._resizeLevel, this._splitParts);

        if (this._extras.length) {
            this._extras.forEach((extra: KMDSymbolParts) => {
                extra.setTextures(this.name, this._resizeLevel, this._splitParts);
            });
        }

        this.checkBgVisibilityForLowSym(false);

        if (this._animation) {
            this._animation.visible = false;

            this._parts.visible = true;
        }
    }

    private checkBgVisibilityForLowSym(val: boolean): void {
        if (["L1", "L2", "L3", "L4", "L5"].indexOf(this.name) === -1) {
            val = true;
        }

        this._parts.toggleBgVisibility(val);
        this._extras.forEach((item: KMDSymbolParts) => {
            item.toggleBgVisibility(val);
        });
    }

    private modifyLine(line: PIXI.Sprite, lineHighlight: PIXI.Sprite, lineTexture: string, effectiveCtr: number, yOffset: number, symHeight: number, reducedY: number): void {
        line.texture = KennethMustDie.assetLoader.getTextures("line/" + lineTexture)[0];
        lineHighlight.texture = KennethMustDie.assetLoader.getTextures("line/line_highlight")[0];

        line.anchor.set(0, 0.5);
        line.position.set(-(KennethMustDie.SYMBOL_WIDTH / 2) + 6, ((yOffset * effectiveCtr) + (yOffset / 2) - (symHeight / 2) + (reducedY / 2)));
        line.scale.x = 0.1;
        this._extraAdditionalLines.push(line);

        lineHighlight.anchor.set(0, 0.5);
        lineHighlight.position = line.position;
        lineHighlight.scale.x = 0.1;
        lineHighlight.blendMode = PIXI.BLEND_MODES.ADD;
    }
}